import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
// Kaizen Dashboard components
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "30%", md: "30%" },
  maxHeight: "90vh",
  overflowY: "auto",
  p: 3,
};

export default function ModalControlCalidad({ open, handleClose, idProducto, idControl, refetch }) {
  const [getProducto, { loading }] = useLazyQuery(gql`
    query getProducto($id: ID!) {
      producto(id: $id) {
        id
        nombre
        ingredientes {
          id
          cantidad
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
          materiaPrima {
            id
            nombre
            tipoUnidad {
              id
              nombre
              abreviatura
              multiplicador
            }
          }
        }
      }
    }
  `);
  const [getControl, { loading: loadingControl }] = useLazyQuery(
    gql`
      query controlCalidadProducto($id: ID!) {
        controlCalidadProducto(id: $id) {
          id
          fecha
          estado
          resultado
          observaciones
          usuario {
            nombre
            apellido
          }
          detalles {
            id
            materiaPrima {
              id
              nombre
              tipoUnidad {
                id
                nombre
                abreviatura
                multiplicador
              }
            }
            tipoUnidad {
              id
              nombre
              abreviatura
              multiplicador
            }
            cantidadEsperada
            cantidadReal
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );
  const [saveControlCalidadProducto, { loading: loadingSave }] = useMutation(gql`
    mutation saveControlCalidadProducto($input: ControlCalidadProductoInput!) {
      saveControlCalidadProducto(input: $input) {
        id
      }
    }
  `);
  const [deleteControlCalidadProducto, { loading: loadingDelete }] = useMutation(gql`
    mutation deleteControlCalidadProducto($id: ID!) {
      deleteControlCalidadProducto(id: $id){
        id
      }
    }
  `);

  const [ingredientes, setIngredientes] = useState([]);
  const [editando, setEditando] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [controlCalidad, setControlCalidad] = useState({
    fecha: dayjs().format("YYYY-MM-DD"),
  });
  const { handleSnackbar } = useContext(MessageManager);

  useEffect(() => {
    setEditando(false);
    setIngredientes([]);
    setUsuario(null);
    if (idProducto && open) {
      getProducto({ variables: { id: idProducto } }).then((res) => {
        let newIngredientes = [];
        res.data.producto.ingredientes.forEach((ingrediente) => {
          let newIngrediente = {
            id: ingrediente.id,
            idDetalle: null,
            materiaPrima: ingrediente.materiaPrima,
            nombre: ingrediente.materiaPrima.nombre,
            cantidad: ingrediente.cantidad,
            tipoUnidad: ingrediente.tipoUnidad,
          };
          newIngredientes.push(newIngrediente);
        });
        setIngredientes(newIngredientes);
      });
      if (idControl) {
        getControl({ variables: { id: idControl } }).then((res) => {
          setControlCalidad({
            fecha: dayjs(res.data.controlCalidadProducto.fecha).format("YYYY-MM-DD"),
            resultado: res.data.controlCalidadProducto.resultado,
            estado: res.data.controlCalidadProducto.estado,
            observaciones: res.data.controlCalidadProducto.observaciones,
          });
          setUsuario(res.data.controlCalidadProducto.usuario);
          let newIngredientes = [];
          res.data.controlCalidadProducto.detalles.forEach((ingrediente) => {
            let newIngrediente = {
              id: ingrediente.id,
              idDetalle: ingrediente.id,
              materiaPrima: ingrediente.materiaPrima,
              nombre: ingrediente.materiaPrima.nombre,
              cantidad: ingrediente.cantidadEsperada,
              tipoUnidad: ingrediente.tipoUnidad,
              real: ingrediente.cantidadReal,
            };
            newIngredientes.push(newIngrediente);
          });
          setIngredientes(newIngredientes);
        });
      } else {
        setControlCalidad({
          fecha: dayjs().format("YYYY-MM-DD"),
        });
      }
    }
  }, [idProducto, open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox pb={2} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6">{`${
              idControl ? "Editar" : "Nuevo"
            } control de calidad`}</SoftTypography>
            <SoftBox>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <SoftBox p={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <SoftBox display="flex" gap={2} width="100%">
                    <SoftBox width="100%">
                      <InputLabel htmlFor="fecha">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha
                        </SoftTypography>
                      </InputLabel>
                      {idControl && !editando ? (
                        <SoftTypography component="label" variant="h6" fontWeight="bold">
                          {controlCalidad?.fecha}
                        </SoftTypography>
                      ) : (
                        <SoftInput
                          type="date"
                          placeholder="Fecha"
                          name="fecha"
                          value={controlCalidad?.fecha}
                          onChange={(e) =>
                            setControlCalidad({ ...controlCalidad, fecha: e.target.value })
                          }
                        />
                      )}
                    </SoftBox>
                    <SoftBox width="100%">
                      <InputLabel htmlFor="resultado">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Puntaje{" "}
                          <SoftTypography component="label" variant="caption">
                            (0-100)
                          </SoftTypography>
                        </SoftTypography>
                      </InputLabel>
                      {idControl && !editando ? (
                        <SoftTypography component="label" variant="h6" fontWeight="bold">
                          {controlCalidad?.resultado}
                        </SoftTypography>
                      ) : (
                        <SoftInputNumber
                          placeholder="Puntaje"
                          name="resultado"
                          value={controlCalidad?.resultado || null}
                          error={controlCalidad?.resultado < 0 || controlCalidad?.resultado > 100}
                          onChange={(e) =>
                            setControlCalidad({ ...controlCalidad, resultado: e.target.value })
                          }
                        />
                      )}
                    </SoftBox>
                  </SoftBox>
                  <SoftBox display="flex" gap={2} width="100%">
                    <SoftBox width={idControl && !editando ? "50%" : "100%"}>
                      <InputLabel htmlFor="estado">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Estado
                        </SoftTypography>
                      </InputLabel>
                      {idControl && !editando ? (
                        <SoftTypography component="label" variant="h6" fontWeight="bold">
                          {controlCalidad?.estado}
                        </SoftTypography>
                      ) : (
                        <Select
                          name="estado"
                          value={controlCalidad?.estado}
                          onChange={(e) => {
                            setControlCalidad({ ...controlCalidad, estado: e.target.value });
                          }}
                        >
                          <MenuItem value={"Aprobado"}>Aprobado</MenuItem>
                          <MenuItem value={"No aprobado"}>No aprobado</MenuItem>
                        </Select>
                      )}
                    </SoftBox>
                    {idControl && !editando && (
                      <SoftBox width="50%">
                        <InputLabel htmlFor="estado">
                          <SoftTypography component="label" variant="caption" fontWeight="bold">
                            Creado por
                          </SoftTypography>
                        </InputLabel>
                        <SoftTypography component="label" variant="h6" fontWeight="bold">
                          {`${usuario?.nombre} ${usuario?.apellido}`}
                        </SoftTypography>
                      </SoftBox>
                    )}
                  </SoftBox>
                  <SoftBox width="100%">
                    <InputLabel htmlFor="observaciones">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Observaciones
                      </SoftTypography>
                    </InputLabel>
                    {idControl && !editando ? (
                      <SoftTypography component="label" variant="h6" fontWeight="bold">
                        {controlCalidad?.observaciones || "Sin observaciones"}
                      </SoftTypography>
                    ) : (
                      <SoftInput
                        placeholder="Observaciones"
                        name="observaciones"
                        multiline
                        rows={4}
                        value={controlCalidad?.observaciones || ""}
                        onChange={(e) =>
                          setControlCalidad({ ...controlCalidad, observaciones: e.target.value })
                        }
                      />
                    )}
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <SoftBox p={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <SoftBox display="flex" justifyContent="space-between" width="100%">
                    <SoftTypography variant="h6">Ingrediente</SoftTypography>
                    <SoftBox display="flex" gap={2} alignItems="center">
                      <SoftBox width="100px" display="flex" justifyContent="center">
                        <SoftTypography variant="h6">Esperado</SoftTypography>
                      </SoftBox>
                      <SoftBox width="120px" display="flex" justifyContent="center">
                        <SoftTypography variant="h6">Real</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                  {loading || loadingControl ? (
                    <SoftBox
                      width="100%"
                      display="flex"
                      justifyContent="center"
                      flexDirection="column"
                    >
                      <Skeleton variant="text" width="100%" />
                      <Skeleton variant="text" width="100%" />
                    </SoftBox>
                  ) : ingredientes.length > 0 ? (
                    ingredientes.map((ingrediente) => (
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        key={ingrediente.id}
                      >
                        <SoftTypography variant="h7" fontWeight="regular">
                          {ingrediente.nombre}
                        </SoftTypography>
                        <SoftBox display="flex" gap={2} alignItems="center">
                          <SoftBox width="100px" display="flex" justifyContent="center">
                            <SoftTypography
                              variant="h7"
                              fontWeight="regular"
                            >{`${ingrediente.cantidad} ${ingrediente.tipoUnidad.abreviatura}`}</SoftTypography>
                          </SoftBox>
                          <SoftBox width="120px" display="flex" justifyContent="center">
                            {idControl && !editando ? (
                              <SoftTypography
                                variant="h7"
                                fontWeight="regular"
                              >{`${ingrediente.real} ${ingrediente.tipoUnidad.abreviatura}`}</SoftTypography>
                            ) : (
                              <SoftInputNumber
                                name="cantidad"
                                value={ingrediente.real}
                                error={ingrediente.real < 0}
                                onChange={(e) =>
                                  setIngredientes(
                                    ingredientes.map((item) =>
                                      item.id === ingrediente.id
                                        ? { ...item, real: e.target.value }
                                        : item
                                    )
                                  )
                                }
                                icon={{
                                  component: (
                                    <SoftBox
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      <SoftTypography variant="h6">
                                        {ingrediente?.tipoUnidad?.abreviatura}
                                      </SoftTypography>
                                    </SoftBox>
                                  ),
                                  direction: "right",
                                }}
                              />
                            )}
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                    ))
                  ) : (
                    <SinDatos />
                  )}
                </SoftBox>
              </Card>
            </Grid>
            {editando || !idControl ? (
              <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
                <SoftButton
                  color="uproGreen"
                  fullWidth
                  circular
                  disabled={
                    loading ||
                    !controlCalidad?.fecha ||
                    !controlCalidad?.resultado ||
                    loadingDelete ||
                    !controlCalidad?.estado ||
                    ingredientes.some((ingrediente) =>  ingrediente?.real == null) ||
                    loadingSave
                  }
                  onClick={() => {
                    saveControlCalidadProducto({
                      variables: {
                        input: {
                          id: idControl,
                          idProducto,
                          ...controlCalidad,
                          detalles: ingredientes.map((ingrediente) => ({
                            id: ingrediente.idDetalle,
                            idMateriaPrima: ingrediente.materiaPrima.id,
                            idTipoUnidad: ingrediente.tipoUnidad.id,
                            cantidadEsperada: ingrediente.cantidad,
                            cantidadReal: ingrediente.real,
                          })),
                        },
                      },
                    })
                      .then(() => {
                        handleSnackbar("Registro guardado correctamente", "success");
                        refetch();
                        handleClose();
                      })
                      .catch((error) => {
                        handleSnackbar("Error al guardar el registro", "error");
                        console.error(error);
                      });
                  }}
                >
                  {loadingSave ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <Icon sx={{ fontWeight: "light" }}>save</Icon>
                  )}
                  &nbsp;Guardar
                </SoftButton>
                {idControl && (
                  <SoftButton
                    color="primary"
                    fullWidth
                    circular
                    disabled={
                      loading ||
                      !controlCalidad?.fecha ||
                      !controlCalidad?.resultado ||
                      !controlCalidad?.estado ||
                      loadingDelete ||
                      loadingSave
                    }
                    onClick={() => {
                      deleteControlCalidadProducto({
                        variables: {
                          id: idControl,
                        },
                      })
                        .then(() => {
                          handleSnackbar("Registro eliminado correctamente", "success");
                          refetch();
                          handleClose();
                        })
                        .catch((error) => {
                          handleSnackbar("Error al eliminar el registro", "error");
                          console.error(error);
                        });
                    }}
                  >
                    {loadingDelete ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>delete</Icon>
                    )}
                    &nbsp;Eliminar
                  </SoftButton>
                )}
              </Grid>
            ) : (
              <Grid item xs={12}>
                <SoftButton color="uproYellow" fullWidth circular onClick={() => setEditando(true)}>
                  <Icon sx={{ fontWeight: "light" }}>edit</Icon>
                  &nbsp;Editar
                </SoftButton>
              </Grid>
            )}
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalControlCalidad.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idProducto: PropTypes.string,
  idControl: PropTypes.string,
  refetch: PropTypes.func.isRequired,
};
